import React, { useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Stack,
  TextField,
  Typography,
  Button,
  Box,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import {
  PageHeader,
  SearchFilterBar,
  DataTable,
  StatusChip,
  flattenPages,
} from '../components';
import { usePurchaseInvoicesPaginated } from '../hooks/usePurchaseInvoices';
import {
  useUploadInvoiceImport,
  useInvoiceImportsPaginated,
} from '../hooks/useInvoiceImports';
import { useBusinessUuid } from '../useBusinessUuid';
import { colors, formatCurrency, formatDate } from '../constants';

export default function PurchaseInvoices() {
  const history = useHistory();
  const businessUuid = useBusinessUuid();
  const fileRef = useRef(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [uploadError, setUploadError] = useState(null);
  const [uploading, setUploading] = useState(false);

  const uploadInvoice = useUploadInvoiceImport();

  const params = useMemo(
    () => ({
      limit: 20,
      status: statusFilter || undefined,
      paymentStatus: paymentStatusFilter || undefined,
      search: search || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [statusFilter, paymentStatusFilter, search, dateFrom, dateTo]
  );

  const query = usePurchaseInvoicesPaginated(params);
  const items = flattenPages(query);

  const importsQuery = useInvoiceImportsPaginated({ limit: 20 });
  const pendingImports = useMemo(
    () => flattenPages(importsQuery).filter((imp) => imp.status !== 'confirmed'),
    [importsQuery]
  );

  const handleFile = async (file) => {
    if (!file) return;
    setUploadError(null);
    // Block the whole page immediately (before/through the upload) so the user
    // never sees just the button spinner; stays up until we navigate away.
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      if (businessUuid) formData.append('businessUuid', businessUuid);
      const created = await uploadInvoice.mutateAsync(formData);
      history.push(`/inventory-management/purchase-invoices/imports/${created.uuid}`);
    } catch (e) {
      setUploadError(e?.response?.data?.message || e.message || 'Failed to upload invoice');
      setUploading(false);
    }
  };

  const columns = [
    {
      key: 'invoiceNumber',
      label: 'Invoice',
      render: (row) => (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600, color: colors.navy }}>
            {row.invoiceNumber}
          </Typography>
          {row.externalInvoiceNumber && (
            <Typography variant="caption" sx={{ color: colors.gray500, display: 'block' }}>
              Vendor Invoice: {row.externalInvoiceNumber}
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: colors.gray500 }}>
            {row.vendorName}
          </Typography>
        </>
      ),
    },
    { key: 'invoiceDate', label: 'Date', render: (row) => formatDate(row.invoiceDate) },
    { key: 'totalAmount', label: 'Total', align: 'right', render: (row) => formatCurrency(row.totalAmount) },
    { key: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
    { key: 'paymentStatus', label: 'Payment', render: (row) => <StatusChip status={row.paymentStatus} /> },
  ];

  const importColumns = [
    {
      key: 'title',
      label: 'Document',
      render: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <AutoAwesomeOutlinedIcon sx={{ color: colors.primary, fontSize: 18 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.extraction?.invoiceNumber
                ? `Invoice ${row.extraction.invoiceNumber}`
                : row.file?.originalName || 'Imported document'}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.gray500 }}>
              {row.extraction?.vendorName || row.file?.originalName || '-'}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    { key: 'createdAt', label: 'Uploaded', render: (row) => formatDate(row.createdAt) },
    { key: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
  ];

  return (
    <>
      <Backdrop
        open={uploading || uploadInvoice.isPending}
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress color="inherit" />
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Uploading and analyzing invoice...
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            This can take a few moments. Please don&apos;t close this page.
          </Typography>
        </Stack>
      </Backdrop>
      <input
        ref={fileRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png"
        style={{ display: 'none' }}
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <PageHeader
        title="Invoices"
        actionLabel="New Invoice"
        actionIcon={<AddIcon />}
        onAction={() => history.push('/inventory-management/purchase-invoices/new')}
        actions={
          <Button
            variant="outlined"
            startIcon={<AutoAwesomeOutlinedIcon />}
            onClick={() => fileRef.current?.click()}
            disabled={uploadInvoice.isPending}
            sx={{ textTransform: 'none', borderColor: colors.border, color: colors.gray700 }}
          >
            {uploadInvoice.isPending ? (
              'Uploading...'
            ) : (
              <>
                Upload (AI)
                <Box
                  component="span"
                  sx={{
                    ml: 0.75,
                    px: 0.5,
                    py: '1px',
                    fontSize: 9,
                    fontWeight: 700,
                    lineHeight: 1.4,
                    letterSpacing: 0.3,
                    textTransform: 'uppercase',
                    borderRadius: 0.75,
                    color: '#fff',
                    backgroundColor: colors.primary,
                  }}
                >
                  beta
                </Box>
              </>
            )}
          </Button>
        }
      />

      {uploadError && (
        <Typography variant="body2" sx={{ color: colors.error, mb: 2 }}>
          {uploadError}
        </Typography>
      )}

      {pendingImports.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: colors.gray500, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            In review / processing ({pendingImports.length})
          </Typography>
          <Box sx={{ mt: 1 }}>
            <DataTable
              columns={importColumns}
              rows={pendingImports}
              onRowClick={(row) =>
                history.push(`/inventory-management/purchase-invoices/imports/${row.uuid}`)
              }
            />
          </Box>
        </Box>
      )}

      <SearchFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search invoices..."
        filters={[
          {
            label: 'Status',
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
              { value: 'draft', label: 'Draft' },
              { value: 'partially_received', label: 'Partially Received' },
              { value: 'received', label: 'Received' },
            ],
          },
          {
            label: 'Payment Status',
            value: paymentStatusFilter,
            onChange: setPaymentStatusFilter,
            options: [
              { value: 'unpaid', label: 'Unpaid' },
              { value: 'partial', label: 'Partially Paid' },
              { value: 'paid', label: 'Paid' },
            ],
          },
        ]}
      >
        <Stack direction="row" spacing={1}>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} sx={{ backgroundColor: '#fff' }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={dateTo} onChange={(e) => setDateTo(e.target.value)} sx={{ backgroundColor: '#fff' }} />
        </Stack>
      </SearchFilterBar>

      <DataTable
        columns={columns}
        rows={items}
        loading={query.isLoading}
        onRowClick={(row) => history.push(`/inventory-management/purchase-invoices/${row.uuid}`)}
        emptyTitle="No invoices found"
        emptyMessage="Create your first invoice or upload one."
        hasNextPage={query.hasNextPage}
        isFetchingNextPage={query.isFetchingNextPage}
        fetchNextPage={query.fetchNextPage}
      />
    </>
  );
}
